/**
 * PixMedia — card PIX 271×116 com ícone oficial via AppIcon.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { AppIcon } from '../../../../../components/icons/AppIcon';
import { FormatTime } from '../FormatTime';
import { StatusIndicator } from '../StatusIndicator';
import { safeParse } from './types.js';
import { MediaFallback } from './MediaFallback.js';

interface PixMediaProps {
    content?: string;
    timestamp?: string | Date;
    status?: string;
    isMe?: boolean;
}

const TYPE_LABEL: Record<string, string> = {
    cpf: 'CPF',
    cnpj: 'CNPJ',
    phone: 'Telefone',
    email: 'E-mail',
    evp: 'Chave aleatória',
};

export const PixMedia: React.FC<PixMediaProps> = ({
    content,
    timestamp,
    status,
    isMe = false,
}) => {
    const [copied, setCopied] = useState(false);

    const data = useMemo(() => {
        const parsed = safeParse(content);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed as { pixKey?: string; pixType?: string; pixName?: string; type?: string };
    }, [content]);

    const pixKey = data?.pixKey?.trim() || '';
    const pixType = (data?.pixType || data?.type || '').toLowerCase();
    const pixName = data?.pixName?.trim() || 'Chave';
    const typeLabel = TYPE_LABEL[pixType] || 'Chave';

    const handleCopy = useCallback(async () => {
        if (!pixKey) return;
        try {
            await navigator.clipboard.writeText(pixKey);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch {
            /* ignore */
        }
    }, [pixKey]);

    if (!pixKey) {
        return <MediaFallback type="document" fallbackLabel="Chave PIX inválida" />;
    }

    return (
        <article
            className="chat-rich-card m-1 flex h-[116px] w-[271px] max-w-[calc(100%-0.5rem)] flex-col overflow-hidden rounded-xl border border-subtle bg-card shadow-xs"
            aria-label={`Chave de ${pixName}`}
        >
            <header className="mx-1 mt-1 flex shrink-0 items-start gap-2 rounded-sm bg-surface-subtle px-1.5 pb-2 pt-1">
                <span
                    className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-card"
                    aria-hidden="true"
                >
                    <AppIcon name="pix" className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold leading-tight text-default">
                        {pixName}
                    </div>
                    <p className="mt-0 line-clamp-2 break-all text-[12px] leading-tight text-default">
                        <span className="text-muted">{typeLabel}:</span>{' '}
                        <span className="font-mono">{pixKey}</span>
                    </p>
                </div>
            </header>

            {/* Após o hover: horário próximo, sem colar */}
            <div className="flex h-3.5 shrink-0 items-center justify-end bg-card px-1.5">
                {timestamp != null && (
                    <div className="flex items-center gap-0.5 text-muted">
                        <FormatTime date={timestamp} />
                        <StatusIndicator status={status} isMe={isMe} variant="muted" />
                    </div>
                )}
            </div>

            <button
                type="button"
                onClick={handleCopy}
                className="mt-auto flex h-9 min-h-9 w-full shrink-0 items-center justify-center gap-1.5 border-t-[1px] border-subtle bg-card text-xs font-semibold text-success-600 transition-colors hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border active:bg-surface-subtle"
            >
                <AppIcon name={copied ? 'check' : 'copy'} className="h-3.5 w-3.5 text-success-600" />
                <span>{copied ? 'Chave copiada' : 'Copiar chave pix'}</span>
            </button>
        </article>
    );
};
